import { useCallback, useEffect, useRef, useState } from "react";
import {
  DailyAudio,
  DailyVideo,
  useAppMessage,
  useDaily,
  useDailyEvent,
  useLocalSessionId,
  useMeetingState,
  useParticipantIds,
  useScreenShare,
} from "@daily-co/daily-react";
import AnnotationOverlay, {
  ANNOTATION_MSG,
  ANNOTATION_COLORS,
  DEFAULT_ANNOTATION_COLOR,
} from "./AnnotationOverlay.jsx";
import styles from "./LessonRoom.module.css";

export default function LessonCall({ role, onLeave }) {
  const daily = useDaily();
  const meetingState = useMeetingState();
  const localSessionId = useLocalSessionId();
  const participantIds = useParticipantIds({ sort: "joined_at" });
  const { screens, isSharingScreen, startScreenShare, stopScreenShare } =
    useScreenShare();

  const screenVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [tool, setTool] = useState("none");
  const [color, setColor] = useState(DEFAULT_ANNOTATION_COLOR);
  const [shapes, setShapes] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [pipActive, setPipActive] = useState(false);
  /** Teacher source of truth; student mirrors via app-message. Default: locked. */
  const [studentDrawAllowed, setStudentDrawAllowed] = useState(false);
  const studentDrawAllowedRef = useRef(false);

  const remoteIds = useParticipantIds({ filter: "remote" });
  const remoteSessionId = remoteIds[0] || null;
  const pipSupported =
    typeof document !== "undefined" && Boolean(document.pictureInPictureEnabled);

  const activeScreen = screens[0] || null;
  const hasScreen = Boolean(activeScreen);
  const canAnnotate = role === "teacher" || studentDrawAllowed;
  const toolsEnabled = hasScreen && canAnnotate;

  const broadcastPermission = useCallback(
    (allowed, send) => {
      if (typeof send !== "function") return;
      send(
        {
          type: ANNOTATION_MSG,
          action: "set-draw-permission",
          allowed: Boolean(allowed),
        },
        "*"
      );
    },
    []
  );

  const handleRemoteAnnotation = useCallback(
    (ev, sendAppMessageFn) => {
      const data = ev?.data;
      if (!data || data.type !== ANNOTATION_MSG) return;

      if (data.action === "set-draw-permission") {
        if (role === "student") {
          const allowed = Boolean(data.allowed);
          setStudentDrawAllowed(allowed);
          if (!allowed) setTool("none");
        }
        return;
      }

      if (data.action === "request-draw-permission") {
        if (role === "teacher") {
          broadcastPermission(studentDrawAllowedRef.current, sendAppMessageFn);
        }
        return;
      }

      if (data.action === "clear") {
        setShapes([]);
        return;
      }

      if (
        data.tool === "line" &&
        typeof data.x1 === "number" &&
        typeof data.y1 === "number" &&
        typeof data.x2 === "number" &&
        typeof data.y2 === "number"
      ) {
        setShapes((prev) => {
          if (data.id && prev.some((s) => s.id === data.id)) return prev;
          return [
            ...prev,
            {
              id: data.id || `${Date.now()}`,
              tool: "line",
              color: data.color || DEFAULT_ANNOTATION_COLOR,
              x1: data.x1,
              y1: data.y1,
              x2: data.x2,
              y2: data.y2,
            },
          ];
        });
        return;
      }

      if (
        data.tool === "stroke" &&
        Array.isArray(data.points) &&
        data.points.length >= 2
      ) {
        setShapes((prev) => {
          if (data.id && prev.some((s) => s.id === data.id)) return prev;
          return [
            ...prev,
            {
              id: data.id || `${Date.now()}`,
              tool: "stroke",
              color: data.color || DEFAULT_ANNOTATION_COLOR,
              points: data.points,
            },
          ];
        });
      }
    },
    [role, broadcastPermission]
  );

  const sendAppMessage = useAppMessage({
    onAppMessage: handleRemoteAnnotation,
  });

  useEffect(() => {
    studentDrawAllowedRef.current = studentDrawAllowed;
  }, [studentDrawAllowed]);

  useDailyEvent(
    "participant-joined",
    useCallback(
      (ev) => {
        if (role !== "teacher") return;
        if (ev?.participant?.local) return;
        broadcastPermission(studentDrawAllowedRef.current, sendAppMessage);
      },
      [role, broadcastPermission, sendAppMessage]
    )
  );

  useEffect(() => {
    if (role !== "student") return;
    if (meetingState !== "joined-meeting") return;
    sendAppMessage(
      { type: ANNOTATION_MSG, action: "request-draw-permission" },
      "*"
    );
  }, [role, meetingState, sendAppMessage]);

  useEffect(() => {
    if (!toolsEnabled && tool !== "none") setTool("none");
  }, [toolsEnabled, tool]);

  const handleClear = () => {
    if (!canAnnotate) return;
    setShapes([]);
    sendAppMessage({ type: ANNOTATION_MSG, action: "clear" }, "*");
  };

  const toggleStudentDraw = () => {
    if (role !== "teacher") return;
    const next = !studentDrawAllowed;
    setStudentDrawAllowed(next);
    studentDrawAllowedRef.current = next;
    broadcastPermission(next, sendAppMessage);
  };

  const toggleMic = async () => {
    if (!daily) return;
    const next = !micOn;
    await daily.setLocalAudio(next);
    setMicOn(next);
  };

  const toggleCam = async () => {
    if (!daily) return;
    const next = !camOn;
    await daily.setLocalVideo(next);
    setCamOn(next);
  };

  const toggleScreenShare = () => {
    if (isSharingScreen) stopScreenShare();
    else startScreenShare();
  };

  useEffect(() => {
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    document.addEventListener("enterpictureinpicture", onEnter);
    document.addEventListener("leavepictureinpicture", onLeave);
    return () => {
      document.removeEventListener("enterpictureinpicture", onEnter);
      document.removeEventListener("leavepictureinpicture", onLeave);
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
    };
  }, []);

  const toggleStudentPip = async () => {
    if (!pipSupported) return;
    const video = remoteVideoRef.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        return;
      }
      if (video.paused) {
        await video.play().catch(() => {});
      }
      await video.requestPictureInPicture();
    } catch (err) {
      console.error("Picture-in-picture failed:", err);
    }
  };

  const cameraIds = participantIds.filter(Boolean).slice(0, 4);

  let hint;
  if (!hasScreen) {
    hint = "Annotation tools unlock when a screen is shared.";
  } else if (role === "student" && !studentDrawAllowed) {
    hint = "Waiting for the teacher to allow drawing…";
  } else {
    hint = "Pick a color, then Line or Draw. Clear removes all marks.";
  }

  return (
    <div className={styles.main}>
      <DailyAudio />
      {meetingState === "joining-meeting" && (
        <div className={styles.statusBanner}>Joining call…</div>
      )}
      {meetingState === "error" && (
        <div className={styles.errorBanner}>
          Could not join the call. Check your link or try again.
        </div>
      )}

      {role === "teacher" && !hasScreen && (
        <div className={styles.statusBanner}>
          Tip: share a PDF or other window — not this tab — to avoid a mirror
          loop. Use Pop out student video to keep seeing them while teaching.
        </div>
      )}
      {role === "student" && !hasScreen && (
        <div className={styles.statusBanner}>
          Waiting for the teacher to share their screen…
        </div>
      )}

      <div className={styles.stageWrap}>
        {hasScreen ? (
          <>
            <div className={styles.stage}>
              <DailyVideo
                ref={screenVideoRef}
                sessionId={activeScreen.session_id}
                type="screenVideo"
                fit="contain"
                className={styles.screenVideo}
                automirror={false}
              />
              <AnnotationOverlay
                videoRef={screenVideoRef}
                enabled={hasScreen}
                tool={toolsEnabled ? tool : "none"}
                color={color}
                shapes={shapes}
                setShapes={setShapes}
                sendAppMessage={sendAppMessage}
                canAnnotate={toolsEnabled}
              />
            </div>
            <div className={styles.cameras}>
              {cameraIds.map((id) => (
                <div key={id} className={styles.cameraTile}>
                  <DailyVideo
                    ref={id === remoteSessionId ? remoteVideoRef : undefined}
                    sessionId={id}
                    type="video"
                    fit="cover"
                    automirror={id === localSessionId}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className={styles.camerasLarge}>
            {cameraIds.length === 0 ? (
              <div className={styles.placeholder}>Connecting cameras…</div>
            ) : (
              cameraIds.map((id) => (
                <div key={id} className={styles.cameraTileLarge}>
                  <DailyVideo
                    ref={id === remoteSessionId ? remoteVideoRef : undefined}
                    sessionId={id}
                    type="video"
                    fit="cover"
                    automirror={id === localSessionId}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.toolGroup}>
          <button type="button" className={styles.btn} onClick={toggleMic}>
            {micOn ? "Mute" : "Unmute"}
          </button>
          <button type="button" className={styles.btn} onClick={toggleCam}>
            {camOn ? "Camera off" : "Camera on"}
          </button>
          {role === "teacher" && (
            <button
              type="button"
              className={`${styles.btn} ${isSharingScreen ? styles.btnActive : ""}`}
              onClick={toggleScreenShare}
            >
              {isSharingScreen ? "Stop sharing" : "Share screen"}
            </button>
          )}
          {role === "teacher" && (
            <button
              type="button"
              className={`${styles.btn} ${
                studentDrawAllowed ? styles.btnActive : ""
              }`}
              onClick={toggleStudentDraw}
            >
              {studentDrawAllowed
                ? "Student drawing: on"
                : "Student drawing: off"}
            </button>
          )}
          {role === "teacher" && pipSupported && (
            <button
              type="button"
              className={`${styles.btn} ${pipActive ? styles.btnActive : ""}`}
              disabled={!remoteSessionId}
              title="Float the student’s camera while you teach from a PDF window (share the PDF, not this tab)"
              onClick={toggleStudentPip}
            >
              {pipActive ? "Close student PiP" : "Pop out student video"}
            </button>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.toolGroup}>
          <button
            type="button"
            className={`${styles.btn} ${tool === "line" ? styles.btnActive : ""}`}
            disabled={!toolsEnabled}
            onClick={() => setTool((t) => (t === "line" ? "none" : "line"))}
          >
            Line
          </button>
          <button
            type="button"
            className={`${styles.btn} ${tool === "stroke" ? styles.btnActive : ""}`}
            disabled={!toolsEnabled}
            onClick={() => setTool((t) => (t === "stroke" ? "none" : "stroke"))}
          >
            Draw
          </button>
          <button
            type="button"
            className={styles.btn}
            disabled={!toolsEnabled}
            onClick={handleClear}
          >
            Clear
          </button>
        </div>

        <div className={styles.divider} />

        <div
          className={styles.colorPicker}
          role="group"
          aria-label="Annotation color"
        >
          {ANNOTATION_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`${styles.colorSwatch} ${
                color === c ? styles.colorSwatchActive : ""
              }`}
              style={{ backgroundColor: c }}
              disabled={!toolsEnabled}
              aria-label={`Color ${c}`}
              aria-pressed={color === c}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        <span className={styles.hint}>{hint}</span>

        <button
          type="button"
          className={`${styles.btn} ${styles.btnDanger} ${styles.leaveBtn}`}
          onClick={onLeave}
        >
          Leave
        </button>
      </div>
    </div>
  );
}
